import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

// Layouts
import LandingLayout from './components/layouts/LandingLayout'
import DashboardLayout from './components/layouts/DashboardLayout'
import AuthLayout from './components/layouts/AuthLayout'

// Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/auth/LoginPage'
import SignupPage from './pages/auth/SignupPage'
import ActivatePage from './pages/auth/ActivatePage'
import OAuthCallbackPage from './pages/auth/OAuthCallbackPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import ProjectsPage from './pages/projects/ProjectsPage'
import OpportunitiesPage from './pages/opportunities/OpportunitiesPage'
import PendingApprovalsPage from './pages/approvals/PendingApprovalsPage'
import AnalyticsPage from './pages/analytics/AnalyticsPage'
import AnalyticsDashboard from './pages/analytics/AnalyticsDashboard'
import IntelligencePage from './pages/intelligence/IntelligencePage'
import SettingsPage from './pages/settings/SettingsPage'
import { CronMonitoringPage } from './pages/dashboard/CronMonitoringPage'
import LoginDebug from './pages/LoginDebug'
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage'
import FeaturesPage from './pages/marketing/FeaturesPage'
import PricingPage from './pages/marketing/PricingPage'
import AboutPage from './pages/marketing/AboutPage'
import ContactPage from './pages/marketing/ContactPage'
import SupportPage from './pages/marketing/SupportPage'

// Hooks
import { useAuth } from './hooks/useAuth'

// Components
import LoadingSpinner from './components/ui/LoadingSpinner'
import ErrorBoundary from './components/ui/ErrorBoundary'
import ScrollToTop from './components/ScrollToTop'

function App() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LandingLayout>
                  <LandingPage />
                </LandingLayout>
              )
            }
          />

          {/* Marketing Pages */}
          <Route
            path="/features"
            element={
              <LandingLayout>
                <FeaturesPage />
              </LandingLayout>
            }
          />
          <Route
            path="/pricing"
            element={
              <LandingLayout>
                <PricingPage />
              </LandingLayout>
            }
          />
          <Route
            path="/about"
            element={
              <LandingLayout>
                <AboutPage />
              </LandingLayout>
            }
          />
          <Route
            path="/contact"
            element={
              <LandingLayout>
                <ContactPage />
              </LandingLayout>
            }
          />
          <Route
            path="/support"
            element={
              <LandingLayout>
                <SupportPage />
              </LandingLayout>
            }
          />

          {/* Legal Routes */}
          <Route
            path="/privacy-policy"
            element={
              <LandingLayout>
                <PrivacyPolicyPage />
              </LandingLayout>
            }
          />

          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthLayout>
                  <LoginPage />
                </AuthLayout>
              )
            }
          />
          <Route
            path="/login-debug"
            element={<LoginDebug />}
          />
          <Route
            path="/signup"
            element={
              user ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthLayout>
                  <SignupPage />
                </AuthLayout>
              )
            }
          />
          <Route
            path="/oauth/callback"
            element={<OAuthCallbackPage />}
          />
          <Route
            path="/activate"
            element={<ActivatePage />}
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              (() => {
                // Check if this is an OAuth callback with parameters
                const urlParams = new URLSearchParams(window.location.search)
                const isOAuthCallback = urlParams.get('oauth_success') && urlParams.get('jwt_token')

                // Allow OAuth callback to process even without authenticated user
                if (isOAuthCallback) {
                  console.log('🔍 OAuth callback detected in router - allowing dashboard access')
                  return (
                    <DashboardLayout>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <DashboardPage />
                      </motion.div>
                    </DashboardLayout>
                  )
                }

                // Normal authentication check for non-OAuth access
                return user ? (
                  <DashboardLayout>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <DashboardPage />
                    </motion.div>
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" replace />
                )
              })()
            }
          />

          <Route
            path="/projects"
            element={
              user ? (
                <DashboardLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ProjectsPage />
                  </motion.div>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/opportunities"
            element={
              user ? (
                <DashboardLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <OpportunitiesPage />
                  </motion.div>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/approvals"
            element={
              user ? (
                <DashboardLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <PendingApprovalsPage />
                  </motion.div>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/analytics"
            element={
              user ? (
                <DashboardLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AnalyticsDashboard projectId={0} />
                  </motion.div>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/intelligence"
            element={
              user ? (
                <DashboardLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <IntelligencePage />
                  </motion.div>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/settings"
            element={
              user ? (
                <DashboardLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SettingsPage />
                  </motion.div>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/dashboard/cron-monitoring"
            element={
              user ? (
                <DashboardLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CronMonitoringPage />
                  </motion.div>
                </DashboardLayout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </ErrorBoundary>
  )
}

export default App